import { z } from 'zod';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isPlainObject from 'lodash/isPlainObject';

/**
 * @param {Object} formData - The object to pick properties from.
 * @param {z.ZodObject} schema - The Zod schema to pick from.
 * @returns {z.ZodObject} - A new Zod schema with shape as that of the object
 */
export function deepSchemaPick(formData, schema) {
  const picked = z.object({});

  if (!formData || !schema) return picked;

  Object.keys(formData).forEach(key => {
    if (key in schema.shape) {
      if (schema.shape[key] instanceof z.ZodObject) {
        picked.shape[key] = deepSchemaPick(formData[key], schema.shape[key]);
      } else {
        picked.shape[key] = schema.shape[key];
      }
    }
  });
  return picked;
}

/**
 * @param {Object} formData - The updates object to check for error paths.
 */
export function getErrorPaths(formData) {
  if (!_isPlainObject(formData) || _isEmpty(formData)) {
    return [];
  }

  return Object.keys(formData).reduce((acc, key) => {
    if (_isPlainObject(formData[key])) {
      const subPaths = getErrorPaths(formData[key]);
      const errorPaths = subPaths.map(subKey => `${key}.${subKey}`);
      acc.push(key, ...errorPaths);
    } else {
      acc.push(key);
    }
    return acc;
  }, []);
}

/**
 * @param {Object} data - The object to create a non-empty deep copy of.
 * @returns {Object} - A new object that is a deep copy of the input object,
 */
export function getNonEmptyDeepCopy(data) {
  if (!_isPlainObject(data)) return {};

  const copy = {};
  for (const [key, value] of Object.entries(data)) {
    if (_isPlainObject(value)) {
      const nestedCopy = getNonEmptyDeepCopy(value);
      if (!_isEmpty(nestedCopy)) {
        copy[key] = nestedCopy;
      }
    } else {
      copy[key] = value;
    }
  }
  return copy;
}

/**
 * @param {Object} formData - The data to validate.
 * @param {z.ZodObject} schema - The Zod schema to validate against.
 * @returns {Record<string, *>} - An object containing error paths and their messages.
 * @description Validates the data against the schema and returns an object
 */
export function validate(formData, schema) {
  // const validationSchema = deepSchemaPick(formData, schema);
  const { error } = schema.safeParse(formData);
  const issues = _get(error, 'issues', []);
  return issues.reduce((acc, issue) => {
    acc[issue.path.join('.')] = issue.message;
    return acc;
  }, {});
}

/**
 * @param {Object} formData - The data to parse.
 * @param {z.ZodObject} schema - The Zod schema to parse data against.
 * @returns {Record<string, *>} - The parsed data if valid, otherwise an empty object.
 * @description Parses the data using the schema and returns the parsed data.
 */
export function getParsedData(formData, schema) {
  if (!_isPlainObject(formData) || _isEmpty(formData)) return {};

  const validationSchema = deepSchemaPick(formData, schema);
  const { data } = validationSchema.safeParse(formData);
  // console.log(success, data, error);
  return data;
}
